import type { Complex } from "./complex";
import type { View } from "./view";

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;
varying vec2 vUv;
uniform vec2 uCenter;
uniform float uWidth;
uniform float uAspect;
uniform vec2 uC;
uniform float uMaxIter;
uniform float uJulia;
uniform vec3 uPaper;
uniform vec3 uInk;
uniform vec3 uWarm;
uniform vec3 uCool;

vec2 cmul(vec2 a, vec2 b) {
  return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
}

void main() {
  vec2 zplane = vec2(
    uCenter.x + vUv.x * uWidth * 0.5,
    uCenter.y + vUv.y * (uWidth * 0.5 / uAspect)
  );
  vec2 z = mix(vec2(0.0), zplane, uJulia);
  vec2 c = mix(zplane, uC, uJulia);

  float n = 0.0;
  float escaped = 0.0;
  const float R2 = 256.0;
  for (int i = 0; i < 512; i++) {
    if (n >= uMaxIter) break;
    float m2 = dot(z, z);
    if (m2 > R2) {
      escaped = 1.0;
      break;
    }
    z = cmul(z, z) + c;
    n += 1.0;
  }

  if (escaped < 0.5) {
    gl_FragColor = vec4(uInk, 1.0);
    return;
  }

  float logZn = log(dot(z, z)) * 0.5;
  float nu = log(logZn / log(2.0)) / log(2.0);
  float t = clamp((n + 1.0 - nu) / uMaxIter, 0.0, 1.0);
  float band = 0.5 + 0.5 * cos(t * 38.0);
  vec3 wash = mix(uPaper, uCool, 0.28 + 0.22 * band);
  float near = smoothstep(0.0, 0.16, t) * (1.0 - smoothstep(0.08, 0.5, t));
  wash = mix(wash, uWarm, near * 0.85);
  wash = mix(uInk, wash, smoothstep(0.0, 0.035, t));
  gl_FragColor = vec4(wash, 1.0);
}
`;

function compile(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new Error("shader");
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh) ?? "compile failed";
    gl.deleteShader(sh);
    throw new Error(log);
  }
  return sh;
}

function hex(rgb: [number, number, number]): [number, number, number] {
  return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
}

const PAPER = hex([243, 234, 214]);
const INK = hex([18, 21, 28]);
const WARM = hex([198, 69, 42]);
const COOL = hex([44, 85, 89]);

export class FractalGL {
  readonly canvas: HTMLCanvasElement;
  private gl: WebGLRenderingContext;
  private prog: WebGLProgram;
  private loc: Record<string, WebGLUniformLocation | null>;
  private buf: WebGLBuffer;
  private dpr = 1;

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      preserveDrawingBuffer: false,
      powerPreference: "high-performance",
    });
    if (!gl) throw new Error("WebGL is required to draw the sets.");
    this.canvas = canvas;
    this.gl = gl;
    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    const prog = gl.createProgram();
    if (!prog) throw new Error("program");
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.bindAttribLocation(prog, 0, "aPos");
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(prog) ?? "link failed");
    }
    this.prog = prog;
    const buf = gl.createBuffer();
    if (!buf) throw new Error("buffer");
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    this.buf = buf;
    this.loc = {
      uCenter: gl.getUniformLocation(prog, "uCenter"),
      uWidth: gl.getUniformLocation(prog, "uWidth"),
      uAspect: gl.getUniformLocation(prog, "uAspect"),
      uC: gl.getUniformLocation(prog, "uC"),
      uMaxIter: gl.getUniformLocation(prog, "uMaxIter"),
      uJulia: gl.getUniformLocation(prog, "uJulia"),
      uPaper: gl.getUniformLocation(prog, "uPaper"),
      uInk: gl.getUniformLocation(prog, "uInk"),
      uWarm: gl.getUniformLocation(prog, "uWarm"),
      uCool: gl.getUniformLocation(prog, "uCool"),
    };
  }

  resize(cssW: number, cssH: number): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.dpr = dpr;
    const w = Math.max(1, Math.round(cssW * dpr));
    const h = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width === w && this.canvas.height === h) return;
    this.canvas.width = w;
    this.canvas.height = h;
    this.gl.viewport(0, 0, w, h);
  }

  draw(opts: { view: View; c: Complex; maxIter: number; julia: boolean }): void {
    const gl = this.gl;
    const cssW = this.canvas.clientWidth || this.canvas.width;
    const cssH = this.canvas.clientHeight || this.canvas.height;
    this.resize(cssW, cssH);
    const aspect = Math.max(cssW / Math.max(cssH, 1), 1e-6);
    gl.useProgram(this.prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(this.loc.uCenter, opts.view.center.re, opts.view.center.im);
    gl.uniform1f(this.loc.uWidth, opts.view.width);
    gl.uniform1f(this.loc.uAspect, aspect);
    gl.uniform2f(this.loc.uC, opts.c.re, opts.c.im);
    gl.uniform1f(this.loc.uMaxIter, Math.min(512, Math.max(8, opts.maxIter)));
    gl.uniform1f(this.loc.uJulia, opts.julia ? 1 : 0);
    gl.uniform3fv(this.loc.uPaper, PAPER);
    gl.uniform3fv(this.loc.uInk, INK);
    gl.uniform3fv(this.loc.uWarm, WARM);
    gl.uniform3fv(this.loc.uCool, COOL);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}
